import { useEffect, useState } from "react";
import { RotateCcw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Demo-account header button: wipe every job / message / SMS-log row in this
 * workshop and re-seed Jane Driver. Lets Steve run the same live pitch flow
 * back-to-back across multiple dealer meetings without a single click of
 * cleanup between them.
 *
 * Props:
 *   triggerOnly — when true, render NO visible button; only the modal when
 *   confirming. Used by HeaderMoreMenu which provides its own menu-item
 *   trigger and just needs the modal mounted somewhere.
 */
export default function DemoResetButton({ triggerOnly = false }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // When mounted in triggerOnly mode, listen for a custom DOM event so the
  // menu item can open us without prop-drilling. Cheap & decoupled.
  useEffect(() => {
    if (!triggerOnly) return;
    const handler = () => setConfirming(true);
    window.addEventListener("ssp:open-demo-reset", handler);
    return () => window.removeEventListener("ssp:open-demo-reset", handler);
  }, [triggerOnly]);

  const doReset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/demo-reset", {});
      const removed = Object.values(r.data?.counts || {}).reduce((a, b) => a + (b || 0), 0);
      // Re-seed Jane Driver so the dashboard isn't empty
      try { await api.post("/jobs/demo"); } catch { /* not fatal */ }
      toast.success(`Demo reset · ${removed} rows cleared · Jane Driver reseeded`);
      // Hard refresh so every component refetches
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reset demo data");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (!confirming) {
    if (triggerOnly) return null;  // menu provides its own trigger
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary/40 bg-primary/[0.06] hover:bg-primary/[0.15] text-primary text-sm font-bold transition-colors"
        data-testid="demo-reset-btn"
        title="Wipe all jobs in this demo workshop and reseed Jane Driver"
      >
        <RotateCcw className="w-4 h-4" /> <span className="hidden lg:inline">Reset demo</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !busy && setConfirming(false)}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl max-w-sm w-full p-6" data-testid="demo-reset-modal">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-warning/15 text-warning grid place-items-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-lg tracking-tight">Reset demo workshop?</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              This will wipe every job, customer message, SMS log row, push subscription, and team invite in this workshop — then reseed Jane Driver as a brand-new customer. Perfect before the next dealer pitch.
            </p>
            <p className="text-[11px] text-muted-foreground/70 mt-2">
              Your branding, templates, and account settings are untouched.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={doReset}
                disabled={busy}
                className="btn-primary flex-1"
                data-testid="demo-reset-confirm-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Yes, reset
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="btn-outline flex-1"
                data-testid="demo-reset-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
